import { TimeSlotGroup } from 'timeSlots/timeSlotsService'
import { CategoryFromApi } from 'products/types'

export interface CategoryTimeSlotInfo {
    id: string
    timeSlotsEnabled?: boolean
    parentId: string | null
}

export const categoryTimeSlotInfo = (categories: CategoryFromApi[]): CategoryTimeSlotInfo[] =>
    categories.map((category) => ({
        id: category.id,
        timeSlotsEnabled: category.timeSlotsEnabled,
        parentId: category.parentCategory?.id || null,
    }))

export interface ProductGroupsInput {
    id: string
    uuid?: string | null
    categoryIds: string[]
}

export type GroupScope = 'product' | 'category' | 'allCategories'

export interface GroupContext {
    scope: GroupScope
    categoryId?: string
    categoryName?: string
    groups: TimeSlotGroup[]
}

export type ProductWindow = 'slot' | 'day'

export interface ApplicableGroups {
    window: ProductWindow
    groupIds: string[]
    contexts: GroupContext[]
}

const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6]

export const groupHasSlots = (group: TimeSlotGroup): boolean => !group.timeSlots || group.timeSlots.length > 0

export const groupInUse = (group: TimeSlotGroup): boolean => group.status !== 'expired' && groupHasSlots(group)

export const distinctWeekdays = (group: TimeSlotGroup): number[] =>
    [...new Set(group.weekdays || [])].filter((day) => day >= 0 && day <= 6).sort((a, b) => a - b)

export const groupRunsEveryWeekday = (group: TimeSlotGroup): boolean => {
    const days = distinctWeekdays(group)
    return days.length === 0 || days.length === ALL_WEEKDAYS.length
}

export const groupHasDates = (group: TimeSlotGroup): boolean => !!group.availableFrom || !!group.availableTo

export const groupIsLimited = (group: TimeSlotGroup): boolean => !groupRunsEveryWeekday(group) || groupHasDates(group)

const groupTargetsProduct = (group: TimeSlotGroup, product: ProductGroupsInput): boolean => {
    const article = group.article
    if (!article) {
        return false
    }
    return (
        article.id === product.id ||
        article.uuid === product.id ||
        (!!product.uuid && (article.id === product.uuid || article.uuid === product.uuid))
    )
}

const groupsOfCategory = (groups: TimeSlotGroup[], categoryId: string): TimeSlotGroup[] =>
    groups.filter((group) => !group.article && (group.productsLists || []).some((list) => list.id === categoryId))

const defaultGroups = (groups: TimeSlotGroup[]): TimeSlotGroup[] =>
    groups.filter((group) => !group.article && (group.productsLists || []).length === 0)

export function categoryGroupContext(
    categoryId: string,
    groups: TimeSlotGroup[],
    categories: CategoryTimeSlotInfo[] = [],
): GroupContext {
    const categoriesById = new Map(categories.map((category) => [category.id, category]))
    const inUse = groups.filter(groupInUse)
    const info = categoriesById.get(categoryId)
    if (info && info.timeSlotsEnabled === false) {
        return { scope: 'category', categoryId, groups: [] }
    }
    const seen = new Set<string>()
    let current: string | null | undefined = categoryId
    while (current && !seen.has(current)) {
        const listId: string = current
        seen.add(listId)
        const own = groupsOfCategory(inUse, listId)
        if (own.length > 0) {
            const list = (own[0].productsLists || []).find((entry) => entry.id === listId)
            return { scope: 'category', categoryId: listId, categoryName: list?.name, groups: own }
        }
        current = categoriesById.get(listId)?.parentId
    }
    return { scope: 'allCategories', groups: defaultGroups(inUse) }
}

export function applicableGroupContexts(
    product: ProductGroupsInput,
    groups: TimeSlotGroup[],
    categories: CategoryTimeSlotInfo[] = [],
): GroupContext[] {
    const inUse = groups.filter(groupInUse)
    const own = inUse.filter((group) => groupTargetsProduct(group, product))
    if (own.length > 0) {
        return [{ scope: 'product', groups: own }]
    }
    const categoryIds = [...new Set(product.categoryIds)]
    if (categoryIds.length === 0) {
        return [{ scope: 'allCategories', groups: defaultGroups(inUse) }]
    }
    return categoryIds.map((categoryId) => categoryGroupContext(categoryId, inUse, categories))
}

export function applicableGroups(
    product: ProductGroupsInput,
    groups: TimeSlotGroup[],
    categories: CategoryTimeSlotInfo[] = [],
): ApplicableGroups {
    const contexts = applicableGroupContexts(product, groups, categories)
    const groupIds: string[] = []
    for (const context of contexts) {
        for (const group of context.groups) {
            if (!groupIds.includes(group.id)) {
                groupIds.push(group.id)
            }
        }
    }
    return { window: groupIds.length > 0 ? 'slot' : 'day', groupIds, contexts }
}

export const contextKey = (context: GroupContext): string =>
    context.groups
        .map((group) => group.id)
        .sort()
        .join('|')
